/**
 * PostPipelineSwap — THE REBUILD FRAME fix (2026-09-27, Atlas sky/post P2).
 *
 * WHY: a graphics apply disposed the live post pipeline and constructed its replacement in place. The replacement's
 * setup is ASYNC (dynamic imports of the display nodes, the MRT scene pass, the TAAU / bloom / AO wiring), and until
 * it resolved `PostProcessingPipeline.render()` fell back to a RAW `renderer.render(scene, camera)` — no bloom, no
 * grade, no fog composite, and into a render target (every probe capture) no output transform at all: a saturated
 * blue sky over a dark specular ground for ~3 s after EVERY apply on WebGL2 (the first on WebGPU), measured by the
 * lighting agent as frame means of 106–111 against a settled 157 (lighting CHECKPOINT 3b).
 *
 * NOW: the replacement is built BESIDE the live pipeline, which keeps rendering; when the replacement's setup
 * resolves it is swapped in atomically (between frames) and the old one is disposed a little later (the caller's
 * `deferDispose` — two frames of grace for an encoder still holding its targets). A newer request supersedes a
 * pending one (the stale build is disposed, never shown). With no live pipeline (the first build) the new one goes
 * live at once — the old behaviour, under the load screen.
 *
 * Pure (no three import) so the unit test drives it with fakes.
 */
export interface SwappablePipeline {
  /** Resolves when the async setup finished (successfully or not). */
  readonly ready: Promise<void>;
  /**
   * True once the setup SUCCEEDED. `ready` resolves on failure too (a display node that throws, a build disposed
   * mid-setup), so the swap reads this before putting a replacement live. Optional: a pipeline without it is trusted.
   */
  readonly isReady?: boolean;
  enabled: boolean;
  dispose(): void;
}

export class PostPipelineSwap<P extends SwappablePipeline> {
  private pending: P | null = null;
  /** Swaps completed (diagnostics). */
  swaps = 0;
  /** Pending builds superseded by a newer request before they were ready (diagnostics). */
  superseded = 0;
  /** Replacements whose setup failed and were dropped while the live pipeline kept drawing (diagnostics). */
  failed = 0;

  constructor(
    private readonly getLive: () => P | null,
    private readonly setLive: (p: P | null) => void,
    private readonly deferDispose: (dispose: () => void) => void = (d) => d(),
  ) {}

  /** The build waiting for its setup (null when none). */
  get pendingPipeline(): P | null { return this.pending; }

  /**
   * Put `next` in line: live at once when nothing renders yet, otherwise swapped in when its setup resolves while
   * the live pipeline keeps drawing. Returns `next`.
   */
  submit(next: P): P {
    const live = this.getLive();
    if (!live) { this.setLive(next); return next; }
    if (this.pending && this.pending !== next) {
      const stale = this.pending;
      this.pending = null;
      this.superseded++;
      this.deferDispose(() => stale.dispose());
    }
    this.pending = next;
    const settle = (): void => this.settle(next);
    next.ready.then(settle, settle);
    return next;
  }

  private settle(next: P): void {
    if (this.pending !== next) return; // superseded (and disposed) or the swapper was torn down
    this.pending = null;
    const old = this.getLive();
    // A failed setup must never replace a working chain: `ready` settles on failure too, and swapping a dead
    // pipeline in left `render()` on its raw fallback (no tone map, no grade) until the next apply.
    if (old && old !== next && next.isReady === false) {
      this.failed++;
      this.deferDispose(() => next.dispose());
      return;
    }
    next.enabled = old ? old.enabled : true;
    this.setLive(next);
    this.swaps++;
    if (old && old !== next) this.deferDispose(() => old.dispose());
  }

  /** Teardown: dispose a pending build (the live one is the owner's). */
  dispose(): void {
    const p = this.pending;
    this.pending = null;
    if (p) p.dispose();
  }
}
